import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Contact, ContactNote, EmailLog } from '../types/crm';

interface CRMState {
  contacts: Contact[];
  totalContacts: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  selectedContact: Contact | null;
  contactNotes: ContactNote[];
  emailLogs: EmailLog[];
  fetchContacts: (page?: number) => Promise<void>;
  fetchContactDetails: (contactId: string) => Promise<void>;
  createContact: (contact: Omit<Contact, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateContact: (id: string, contact: Partial<Contact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  addNote: (contactId: string, note: string) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  importContacts: (contacts: Partial<Contact>[]) => Promise<void>;
  exportContacts: () => Promise<Contact[]>;
  clearError: () => void;
}

export const useCRMStore = create<CRMState>((set, get) => ({
  contacts: [],
  totalContacts: 0,
  currentPage: 1,
  loading: false,
  error: null,
  selectedContact: null,
  contactNotes: [],
  emailLogs: [],

  fetchContacts: async (page = 1) => {
    try {
      set({ loading: true, error: null });
      
      const limit = 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      const { data: contacts, count, error } = await supabase
        .from('crm_contacts')
        .select('*', { count: 'exact' })
        .eq('deleted', false)
        .order('last_name', { ascending: true })
        .range(from, to);

      if (error) throw error;

      set({
        contacts: contacts || [],
        totalContacts: count || 0,
        currentPage: page,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching contacts:', error);
      set({ error: 'Failed to load contacts', loading: false });
    }
  },

  fetchContactDetails: async (contactId: string) => {
    try {
      set({ loading: true, error: null });

      // Check if we have a valid contact ID
      if (!contactId) {
        throw new Error('Contact ID is required');
      }

      // Fetch contact details
      const { data: contact, error: contactError } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('id', contactId)
        .eq('deleted', false)
        .single();

      if (contactError) {
        throw new Error(`Failed to fetch contact: ${contactError.message}`);
      }

      if (!contact) {
        throw new Error('Contact not found');
      }

      // Fetch contact notes
      const { data: notes, error: notesError } = await supabase
        .from('crm_contact_notes')
        .select('*')
        .eq('contact_id', contactId)
        .eq('deleted', false)
        .order('created_at', { ascending: false });

      if (notesError) {
        throw new Error(`Failed to fetch notes: ${notesError.message}`);
      }

      // Fetch email logs
      const { data: logs, error: logsError } = await supabase
        .from('crm_email_logs')
        .select('*')
        .eq('contact_id', contactId)
        .order('sent_at', { ascending: false });

      if (logsError) {
        throw new Error(`Failed to fetch email logs: ${logsError.message}`);
      }

      set({
        selectedContact: contact,
        contactNotes: notes || [],
        emailLogs: logs || [],
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching contact details:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load contact details',
        loading: false,
        selectedContact: null,
        contactNotes: [],
        emailLogs: []
      });
    }
  },

  createContact: async (contact) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('crm_contacts')
        .insert([contact])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        contacts: [...state.contacts, data],
        loading: false
      }));

      return data;
    } catch (error) {
      console.error('Error creating contact:', error);
      set({ error: 'Failed to create contact', loading: false });
      throw error;
    }
  },

  updateContact: async (id, contact) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('crm_contacts')
        .update(contact)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        contacts: state.contacts.map(c => c.id === id ? data : c),
        selectedContact: state.selectedContact?.id === id ? data : state.selectedContact,
        loading: false
      }));
    } catch (error) {
      console.error('Error updating contact:', error);
      set({ error: 'Failed to update contact', loading: false });
    }
  },

  deleteContact: async (id) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('crm_contacts')
        .update({ deleted: true })
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        contacts: state.contacts.filter(c => c.id !== id),
        selectedContact: state.selectedContact?.id === id ? null : state.selectedContact,
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting contact:', error);
      set({ error: 'Failed to delete contact', loading: false });
    }
  },

  addNote: async (contactId: string, note: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('crm_contact_notes')
        .insert([{ contact_id: contactId, note }])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        contactNotes: [data, ...state.contactNotes],
        loading: false
      }));
    } catch (error) {
      console.error('Error adding note:', error);
      set({ error: 'Failed to add note', loading: false });
    }
  },

  deleteNote: async (noteId: string) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('crm_contact_notes')
        .update({ deleted: true })
        .eq('id', noteId);

      if (error) throw error;

      set(state => ({
        contactNotes: state.contactNotes.filter(n => n.id !== noteId),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting note:', error);
      set({ error: 'Failed to delete note', loading: false });
    }
  },

  importContacts: async (contacts) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('crm_contacts')
        .insert(contacts)
        .select();

      if (error) throw error;

      await get().fetchContacts(1);
      set({ loading: false });
    } catch (error) {
      console.error('Error importing contacts:', error);
      set({ error: 'Failed to import contacts', loading: false });
    }
  },

  exportContacts: async () => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('deleted', false)
        .order('last_name', { ascending: true });

      if (error) throw error;

      set({ loading: false });
      return data;
    } catch (error) {
      console.error('Error exporting contacts:', error);
      set({ error: 'Failed to export contacts', loading: false });
      return [];
    }
  },

  clearError: () => set({ error: null })
}));